<script>
import Multiselect   from "vue-multiselect";
import locales       from "@/data/locales";
import axios         from "axios";
import Utils         from "@/js/utils";
import DataUtils     from "@/js/data-utils";
import { debounce }  from "lodash";

export default {
  data() {
    return {
      user: {},
      extensionSettings: {}
    };
  },
  props: ["review", "responseSettings"],
  components: { Multiselect },
  async created() {
    this.user = await this.$store.dispatch("user/fetch");
    this.getExtensionSettings();
  },
  methods: {
    ...DataUtils,
    ...Utils.extension,
    async getExtensionSettings() {
      this.extensionSettings = null;
      return this.$store.dispatch("extension/fetchSettings").then(s => {
        this.extensionSettings = Utils.deepClone(s);
        // Language
        if (this.extensionSettings.language) {
          if (this.extensionSettings.language == this.review.language_name) {
            this.responseSettings.language.use_review_language = true;
            this.responseSettings.language.customLanguage = null;
          } else {
            this.responseSettings.language.use_review_language = false;
            this.responseSettings.language.customLanguage = locales[this.extensionSettings.language];
          }
        }

        // Tonality
        if (this.extensionSettings.tonality) {
          if (!/professional|friendly/.test(this.extensionSettings.tonality)) {
            this.responseSettings.customTonality = this.extensionSettings.tonality;
            this.responseSettings.tonality = null;
          }
          else this.responseSettings.tonality = this.extensionSettings.tonality;
        }

        // Mention Property Name
        this.responseSettings.mention_property_name.enabled = !!this.extensionSettings.mention_property_name?.enabled;

        // Sign name
        this.responseSettings.sign_name.enabled = !!this.extensionSettings.sign_name?.enabled;
        this.responseSettings.sign_name.value   = this.extensionSettings.sign_name?.value || this.user.name;

        // Limit words
        this.responseSettings.limit_words.enabled = !!this.extensionSettings.limit_words?.enabled;
        this.responseSettings.limit_words.value   = this.extensionSettings.limit_words?.value || 100;

        // Mention words
        this.responseSettings.words_to_mention.enabled = !!this.extensionSettings.words_to_mention?.enabled;
        this.responseSettings.words_to_mention = { ...(this.responseSettings.words_to_mention || {}), ...(this.extensionSettings.words_to_mention || {}) };

        // Saved responses
        this.responseSettings.saved_responses.options = Utils.deepClone(this.extensionSettings.saved_responses || []);
      });
    },
    async updateExtensionSettings(field, value) {
      await this.$store.commit("extension/saveExtensionSettings", { data: { [field]: Utils.deepClone(value) }});
      const params = this.savedSettings;
      await axios.post("/v3/users/extension_settings", params);
    },
    updateLanguage(value, custom) {
      if (custom) {
        if (this.responseSettings.language.customLanguage) this.responseSettings.language.use_review_language = false;
        else this.responseSettings.language.use_review_language = true;
      } else {
        if (value) this.responseSettings.language.customLanguage = null;
        else this.responseSettings.language.customLanguage = this.$i18n.locale;
      }
      this.updateExtensionSettings("language", this.$t(`locales.${value}`, "en"));
    },
    updateTonality: debounce(function (value, custom) {
      if (custom) {
        if (this.responseSettings.customTonality) this.responseSettings.tonality = "";
        else this.responseSettings.tonality = "professional";
      } else {
        if (value) this.responseSettings.customTonality = "";
        else this.responseSettings.customTonality = "friendly";
      }
      this.updateExtensionSettings("tonality", value);
    }, 300),
    updateField: debounce(async function (field, values = ["enabled", "value"]) {
      const params = Utils.slice(this.responseSettings[field], values);
      await this.updateExtensionSettings(field, params);
    }, 300),
    updateCompanyAlias: debounce(async function () {
      await axios.post(`/v3/companies/${this.review.company.id}/alias`, { alias: this.responseSettings.mention_property_name.value });
      this.updateField("mention_property_name");
    }, 300),
    async getCompanyAlias() {
      if (!this.review.company?.id) return;
      const res = await axios.get(`/v3/companies/${this.review.company.id}/alias`);
      this.responseSettings.mention_property_name.value = res.data.data.alias || this.review.company.name;
    },
    async addWordToMention(val) {
      this.responseSettings.words_to_mention.options  = [...this.responseSettings.words_to_mention.options, val];
      this.responseSettings.words_to_mention.selected = [...this.responseSettings.words_to_mention.selected, val];
      this.updateField("words_to_mention", ["enabled", "options", "selected"]);
    },
    removeWordsToMention(ev, val) {
      ev.stopPropagation();
      this.responseSettings.words_to_mention.options  = this.responseSettings.words_to_mention.options.filter(o => o != val);
      this.responseSettings.words_to_mention.selected = this.responseSettings.words_to_mention.selected.filter(o => o != val);
      this.updateField("words_to_mention", ["enabled", "options", "selected"]);
    },
  },
  computed: {
    languageOptions() {
      return [
        { label: this.$t("daily_operations.reviews.dashboard_languages"), languages: Utils.locales },
        { label: this.$t("general.others"), languages: Object.values(locales) },
      ];
    },
    tonalityOptions() {
      return ["professional", "friendly"].reduce((r, c) => {
        r.push({ text: this.$t(`daily_operations.reviews.${c}`), value: c });
        return r;
      }, []);
    },
    savedSettings() {
      return this.$store.getters["extension/extensionSettings"];
    },
    insideExtension() {
      return this.$store.getters["extension/insideExtension"];
    },
  },
  watch: {
    "responseSettings.mention_property_name.enabled": {
      handler() {
        this.getCompanyAlias();
      },
      immediate: true
    },
    "responseSettings.sign_name.enabled": {
      handler() {
        this.responseSettings.sign_name.value ??= this.user.name;
      },
      immediate: true
    },
    "responseSettings.limit_words.enabled": {
      handler() {
        this.responseSettings.limit_words.value ??= 100;
      },
      immediate: true
    }
  }
};
</script>

<template>
  <div :class="{ 'extension-settings': insideExtension }">
    <!-- Language Preference -->
    <div class="row d-flex align-items-center mb-2">
      <div class="col-12 col-sm-5">
        <label :class="{ 'ps-switch-md': !insideExtension }">{{ $t("daily_operations.reviews.language_preference") }}</label>
      </div>
      <div class="col-12 col-sm-7 align-items-center" :class="{ 'd-flex': review.language }">
        <div class="me-3">
          <BFormCheckbox v-if="review.language" v-model="responseSettings.language.use_review_language" button button-variant="outline-primary" @input="updateLanguage(review.language, false)">
            <div class="d-flex align-items-center">
              <span :class="`flag flag-${locales(review.language, 'language').code}`" class="me-2"></span>
              {{ $t("daily_operations.reviews.review_language", { l: $t(`locales.${review.language}`) }) }}
            </div>
          </BFormCheckbox>
        </div>

        <div class="w-100 pt-1">
          <multiselect
            :class="{ 'selected-language': responseSettings.language.customLanguage }"
            :multiple="false"
            group-values="languages" group-label="label" :group-select="false"
            v-model="responseSettings.language.customLanguage"
            :options="languageOptions"
            :showLabels="false"
            :placeholder="$t('daily_operations.reviews.select_custom_language')"
            :customLabel="l => $t(`locales.${l}`)"
            @select="e => updateLanguage(e, true)"
            @remove="e => updateLanguage(e, true)"
          >
            <template v-slot:singleLabel="props">
              <div v-if="props.option.$groupLabel">
                {{ props.option.$groupLabel }}
              </div>
              <div v-else class="d-flex align-items-center">
                <span :class="`flag flag-${locales(props.option, 'language').code}`" class="me-2"></span>
                <span> {{ locales(props.option).name }} </span>
              </div>
            </template>

            <template v-slot:option="props">
              <div v-if="props.option.$groupLabel">
                {{ props.option.$groupLabel }}
              </div>
              <div v-else class="d-flex align-items-center">
                <span :class="`flag flag-${locales(props.option, 'language').code}`" class="me-2"></span>
                <span> {{ locales(props.option).name }} </span>
              </div>
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <!-- Tonality -->
    <div class="row d-flex align-items-center mb-2">
      <div class="col-12 col-sm-5">
        <label :class="{ 'ps-switch-md': !insideExtension }">{{ $t("daily_operations.reviews.tonality_preference") }}</label>
      </div>
      <div class="col-12 col-sm-7 d-flex align-items-center">
        <BFormRadioGroup
          :class="{ 'me-3': !insideExtension, 'w-100': insideExtension }"
          v-model="responseSettings.tonality"
          :options="tonalityOptions"
          button-variant="outline-primary"
          buttons
          @input="e => updateTonality(e, false)"
        />
        <input type="text" v-if="!insideExtension" class="form-control" :placeholder="$t('daily_operations.reviews.custom_tonality')" v-model="responseSettings.customTonality" @input="e => updateTonality(e.target.value, true)" />
      </div>
      <div v-if="insideExtension" class="col-12 my-2">
        <input type="text" class="form-control" :placeholder="$t('daily_operations.reviews.custom_tonality')" v-model="responseSettings.customTonality" @input="e => updateTonality(e.target.value, true)" />
      </div>
    </div>

    <!-- Mention the property name in the response -->
    <div class="row d-flex align-items-center mb-2">
      <div class="col-12 col-sm-5" :class="insideExtension && 'd-flex align-items-center justify-content-between'">
        <template v-if="insideExtension">
          <label class="font-size-14">{{ $t("daily_operations.reviews.mention_property_name") }}</label>
          <b-form-checkbox switch size="md" v-model="responseSettings.mention_property_name.enabled" @input="updateField('mention_property_name')" />
        </template>
        <template v-else>
          <b-form-checkbox switch v-model="responseSettings.mention_property_name.enabled" size="md" @input="updateField('mention_property_name')">
            <span class="me-2 font-size-14">{{ $t("daily_operations.reviews.mention_property_name") }}</span>
          </b-form-checkbox>
        </template>
      </div>
      <div class="col-12 col-sm-7">
        <div class="position-relative">
          <input type="text" class="form-control inner-icon-input" :placeholder="$t('daily_operations.reviews.property_name_placeholder')" v-model="responseSettings.mention_property_name.value" :disabled="!responseSettings.mention_property_name.enabled" @input="updateCompanyAlias" />
        </div>
      </div>
    </div>

    <!-- Sign with the following name -->
    <div class="row d-flex align-items-center mb-2">
      <div class="col-12 col-sm-5" :class="insideExtension && 'd-flex align-items-center justify-content-between'">
        <template v-if="insideExtension">
          <label class="font-size-14">{{ $t("daily_operations.reviews.sign_with_name") }}</label>
          <b-form-checkbox switch size="md" v-model="responseSettings.sign_name.enabled" @input="updateField('sign_name')" />
        </template>
        <template v-else>
          <b-form-checkbox switch v-model="responseSettings.sign_name.enabled" size="md" @input="updateField('sign_name')" >
            <span class="me-2 font-size-14">{{ $t("daily_operations.reviews.sign_with_name") }}</span>
          </b-form-checkbox>
        </template>
      </div>
      <div class="col-12 col-sm-7">
        <input type="text" class="form-control" :placeholder="$t('daily_operations.reviews.sign_name_placeholder')" v-model="responseSettings.sign_name.value" :disabled="!responseSettings.sign_name.enabled" @input="updateField('sign_name')" />
      </div>
    </div>

    <!-- Limit size -->
    <div class="row d-flex align-items-center mb-2">
      <div class="col-12 col-sm-5" :class="insideExtension && 'd-flex align-items-center justify-content-between'">
        <template v-if="insideExtension">
          <label class="font-size-14">{{ $t("daily_operations.reviews.limit_size") }}</label>
          <b-form-checkbox switch size="md" v-model="responseSettings.limit_words.enabled" @input="updateField('limit_words')" />
        </template>
        <template v-else>
          <b-form-checkbox switch v-model="responseSettings.limit_words.enabled" size="md" @input="updateField('limit_words')">
            <span class="me-2 font-size-14">{{ $t("daily_operations.reviews.limit_size") }}</span>
          </b-form-checkbox>
        </template>
      </div>
      <div class="col-12 col-sm-7">
        <div class="input-group">
          <input type="number" class="form-control" :disabled="!responseSettings.limit_words.enabled" v-model="responseSettings.limit_words.value" aria-describedby="word-limit" @input="updateField('limit_words')" />
          <span class="input-group-text" id="word-limit">{{ $t("daily_operations.reviews.words") }}</span>
        </div>
      </div>
    </div>

    <!-- Mention the following words -->
    <div class="row d-flex align-items-center">
      <div class="col-12 col-sm-5" :class="insideExtension && 'd-flex align-items-center justify-content-between'">
        <template v-if="insideExtension">
          <label class="font-size-14">{{ $t("daily_operations.reviews.mention_words") }}</label>
          <b-form-checkbox switch size="md" v-model="responseSettings.words_to_mention.enabled" @input="updateField('words_to_mention', ['enabled', 'options', 'selected'])" />
        </template>
        <template v-else>
          <b-form-checkbox switch v-model="responseSettings.words_to_mention.enabled" size="md" @input="updateField('words_to_mention', ['enabled', 'options', 'selected'])">
            <div class="font-size-14">{{ $t("daily_operations.reviews.mention_words") }}</div>
          </b-form-checkbox>
        </template>
      </div>
      <div class="col-12 col-sm-7">
        <multiselect
          :class="{ disabled: !responseSettings.words_to_mention.enabled }"
          :multiple="true"
          v-model="responseSettings.words_to_mention.selected"
          :options="responseSettings.words_to_mention.options"
          :showLabels="false"
          :placeholder="$t('filter.select_option')"
          :taggable="true"
          :tag-placeholder="$t('daily_operations.reviews.mention_words_new_tag')"
          @tag="addWordToMention"
          @select="updateField('words_to_mention', ['enabled', 'options', 'selected'])"
          @remove="updateField('words_to_mention', ['enabled', 'options', 'selected'])"
        >
          <template v-slot:singleLabel="props">
            {{ props.label }}
          </template>

          <template v-slot:option="props">
            <div class="d-flex align-items-center justify-content-between">
              <span>
                {{ props.option.label ? props.option.label : props.option }}
              </span>
              <a v-if="!(props.option && props.option.label)" href="javascript:void(0);" @click="e => removeWordsToMention(e, props.option)" v-b-tooltip.hover :title="$t('general.delete')">
                <i class="mdi mdi-trash-can font-size-18"></i>
              </a>
            </div>
          </template>
        </multiselect>
      </div>
    </div>
  </div>
</template>
